import qs from 'qs';

import { USE_API_MAGENTO_MOCK_DATA } from '@constants';
import { buildUrl } from '@helpers/url';

import { api, applyDefaultFilterParam } from '../magento.service';

import { ExportInterface, LocationInterface, PackageDraftInput, Shipment, ShipmentDraftInput } from '@stone/interfaces';

function transformAxiosData(response: any) {
  const data = response?.data;
  const status = response?.status;
  // Magento's error envelope is `{ message, parameters?, trace? }`. Real domain payloads
  // (TrackingEvent, ShipmentTrackingInfo) also carry a `message` field, so don't treat
  // every `message` as an error — require the envelope shape.
  const isMagentoError = typeof data?.message === 'string' && (Array.isArray(data?.parameters) || typeof data?.trace === 'string');
  if ((typeof status === 'number' && status >= 400) || isMagentoError) {
    throw new Error('Request Failure: ' + String(data?.message ?? status));
  }
  return data;
}

class StoneService {
  /* ==== Inventory ==== */
  getStoneInventories(params: any = {}) {
    const query = qs.stringify(applyDefaultFilterParam(params || {}));
    const url = `/stone/inventories${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  saveStoneInventory(data: any) {
    const url = '/stone/inventory';
    return api.post(url, { stoneInventory: data }).then(transformAxiosData);
  }

  /* ==== Shipments ==== */
  getStoneShipments(params: any = {}) {
    const query = qs.stringify(applyDefaultFilterParam(params || {}, 'created_at', 'DESC'));
    const url = `/stone/logistic/shipments${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  getStoneShipmentById(id: string | number) {
    const url = buildUrl('/stone/logistic/shipments/:id', { id });
    return api.get(url).then(transformAxiosData);
  }

  saveStoneShipment(data: Partial<Shipment>) {
    const url = '/stone/logistic/shipment';
    return api.post(url, { shipment: data }).then(transformAxiosData);
  }

  stoneShipmentShip(data: Partial<Shipment>) {
    const url = '/stone/logistic/shipment/ship';
    return api.post(url, { shipment: data }).then(transformAxiosData);
  }

  receiveStoneShipment(receiveRequest: any) {
    const url = '/stone/logistic/shipment/receive';
    return api.post(url, { receiveRequest }).then(transformAxiosData);
  }

  exportStoneShipmentSummary(code: string, format = 'excel'): Promise<ExportInterface> {
    const url = buildUrl('/stone/logistic/shipment/export-summary/:code', { code }, { format });
    return api.get(url).then(transformAxiosData);
  }

  /* ==== Tracking ==== */
  getStoneTrackingInfo(shipmentId: string | number) {
    const url = buildUrl('/stone/logistic/track/:shipmentId', { shipmentId });
    return api.get(url).then(transformAxiosData);
  }

  /* ==== Packages ==== */
  getStonePackages(params: any) {
    const query = qs.stringify(params);
    const url = `/stone/logistic/packages${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  // Loads a shipment's packages. Prefer root-only by default so the caller can lazy-load children via getStonePackageChildren.
  getStonePackagesByShipment(shipmentId: number | string, rootsOnly = true) {
    const filterGroups: any[] = [{ filters: [{ field: 'shipment_id', value: shipmentId, condition_type: 'eq' }] }];
    if (rootsOnly) {
      filterGroups.push({ filters: [{ field: 'parent_id', condition_type: 'null' }] });
    }
    return this.getStonePackages({ searchCriteria: { filterGroups, pageSize: 1000 } });
  }

  // Paginated, filter-aware packages fetch used by the read-only Shipment Detail view.
  // Search spans `name` OR `package_code` via OR'd filters in the same filter_group.
  getStonePackagesByShipmentPaged(
    shipmentId: number | string,
    {
      page,
      pageSize,
      search,
      status,
      sortDirection = 'ASC',
    }: { page: number; pageSize: number; search?: string; status?: number | string; sortDirection?: 'ASC' | 'DESC' },
  ) {
    const filterGroups: any[] = [
      { filters: [{ field: 'shipment_id', value: shipmentId, condition_type: 'eq' }] },
      { filters: [{ field: 'parent_id', condition_type: 'null' }] },
    ];
    if (status !== undefined && status !== '' && status !== null) {
      filterGroups.push({ filters: [{ field: 'status', value: status, condition_type: 'eq' }] });
    }
    const term = (search ?? '').trim();
    if (term) {
      const like = `%${term}%`;
      filterGroups.push({
        filters: [
          { field: 'name', value: like, condition_type: 'like' },
          { field: 'package_code', value: like, condition_type: 'like' },
        ],
      });
    }
    return this.getStonePackages({
      searchCriteria: {
        filterGroups,
        currentPage: page,
        pageSize,
        sortOrders: [{ field: 'package_id', direction: sortDirection }],
      },
    });
  }

  getStonePackageChildren(packageId: number | string) {
    const url = buildUrl('/stone/logistic/packages/:packageId/children', { packageId });
    return api.get(url).then(transformAxiosData);
  }

  getStonePackagesPendingReceive(params: any) {
    const query = qs.stringify(params);
    const url = `/stone/logistic/packages-pending-receive${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  getStonePackageByBarcode(code: string) {
    const url = buildUrl('/stone/logistic/packages/scan-receive/:code', { code });
    return api.get(url).then(transformAxiosData);
  }

  getStoneShipmentByBarcode(code: string) {
    const url = buildUrl('/stone/logistic/shipments/scan-receive/:code', { code });
    return api.get(url).then(transformAxiosData);
  }

  /* ==== Labels ==== */
  generateStoneLabels(labelRequest: any) {
    const url = '/stone/logistic/packages/labels';
    return api.post(url, { labelRequest }).then(transformAxiosData);
  }

  /* ==== Draft Shipment (incremental package workflow, §4.6) ==== */
  // Creates the draft shipment on first call (pass shipment.shipment_id = null) and persists one package.
  // Response: { shipment, package, labels[], packages_search_result }
  createDraftPackage(shipment: ShipmentDraftInput, pkg: PackageDraftInput) {
    const url = '/stone/logistic/shipment/draft/createPackage';
    return api.post(url, { shipment, package: pkg }).then(transformAxiosData);
  }

  // Updates qty / actual_total_carat on an existing draft package. Server resets status to DRAFT.
  // Response: { shipment, package, labels: [], packages_search_result: null }
  updateDraftPackage(shipment: ShipmentDraftInput, pkg: PackageDraftInput) {
    const url = '/stone/logistic/shipment/draft/package';
    return api.put(url, { shipment, package: pkg }).then(transformAxiosData);
  }

  // Response: { shipment, packages_search_result }
  deleteDraftPackage(shipmentId: number | string, packageId: number | string) {
    const url = buildUrl('/stone/logistic/shipment/draft/:shipmentId/package/:packageId', { shipmentId, packageId });
    return api.delete(url).then(transformAxiosData);
  }

  // Server-side bulk clear: single DELETE that wipes every package on a draft shipment
  // and resets totals to 0. Response: { shipment, packages_search_result } with items=[].
  deleteAllDraftPackages(shipmentId: number | string) {
    const url = buildUrl('/stone/logistic/shipment/draft/:shipmentId/packages', { shipmentId });
    return api.delete(url).then(transformAxiosData);
  }

  /* ==== Locations ==== */
  getStoneLocations(params: any = {}) {
    const query = qs.stringify(applyDefaultFilterParam(params));
    const url = `/stone/logistic/locations${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  getStoneLocationOptions() {
    const url = '/stone/logistic/locations/options';
    return api.get(url).then(transformAxiosData);
  }

  getLocationOptions(params: any = {}) {
    const query = qs.stringify(applyDefaultFilterParam(params));
    const url = `/stone/logistic/location-options${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  saveStoneLocation(data: LocationInterface) {
    const url = '/stone/logistic/location';
    return api.post(url, { location: data }).then(transformAxiosData);
  }

  deleteStoneLocation(id: number | string) {
    const url = buildUrl('/stone/logistic/locations/:id', { id });
    return api.delete(url).then(transformAxiosData);
  }

  /* ==== Suppliers ==== */
  getStoneSuppliers(params: any = {}) {
    const query = qs.stringify(applyDefaultFilterParam(params));
    const url = `/stone/logistic/suppliers${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  getStoneSupplierById(id: number | string) {
    const url = buildUrl('/stone/logistic/suppliers/:id', { id });
    return api.get(url).then(transformAxiosData);
  }

  saveStoneSupplier(data: any) {
    if (data.entity_id) {
      const url = buildUrl('/stone/logistic/supplier/:id', { id: data.entity_id });
      return api.put(url, { supplier: data }).then(transformAxiosData);
    }
    return api.post('/stone/logistic/supplier', { supplier: data }).then(transformAxiosData);
  }

  deleteStoneSupplier(id: number | string) {
    const url = buildUrl('/stone/logistic/suppliers/:id', { id });
    return api.delete(url).then(transformAxiosData);
  }

  /* ==== Reports ==== */
  getStoneShipmentReports(params: any = {}) {
    const query = qs.stringify(applyDefaultFilterParam(params));
    const url = `/stone/logistic/shipmentReport${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  refreshShipmentReports() {
    const url = '/stone/logistic/reportRefresh';
    return api.post(url).then(transformAxiosData);
  }

  exportStoneShipmentReports(type: string, format: string, params: any = {}) {
    const query = qs.stringify(applyDefaultFilterParam(params));
    const url = `/stone/logistic/reportExport/${type}/${format}${query ? '?' : ''}${query}`;
    return api.get(url).then(transformAxiosData);
  }

  /* ==== Options / Meta ==== */
  getStoneOptions() {
    const url = '/stones/options';
    return api.get(url).then(transformAxiosData);
  }

  getCarrierOptions() {
    const url = '/stone/logistic/carrier-options';
    return api.get(url).then(transformAxiosData);
  }

  getWarehouseFactories() {
    const url = '/stone/warehouseFactories';
    return api.get(url).then(transformAxiosData);
  }

  getStoneOptionsFlow(filters: any = {}, signal?: AbortSignal) {
    const searchCriteria = {
      filterGroups: Object.keys(filters as Record<string, any>).map((key) => ({
        filters: [
          {
            field: key,
            value: filters[key],
            condition_type: 'eq',
          },
        ],
      })),
    };
    const query = qs.stringify({ searchCriteria });
    const url = `/stone/logistic/optionsFlowFilter${query ? '?' : ''}${query}`;
    return api.get(url, { signal }).then(transformAxiosData);
  }

  getStoneOptionsFlowById(id: string | number, signal?: AbortSignal) {
    const url = buildUrl('/stone/logistic/optionsFlowFilter/:id', { id });
    return api.get(url, { signal }).then(transformAxiosData);
  }
}

// allow override default and mock data
class StoneServiceMock extends StoneService {
  exportStoneShipmentSummary(_code: string, _format = 'excel'): Promise<ExportInterface> {
    return Promise.resolve({
      file_content:
        'PD94bWwgdmVyc2lvbj0iMS4wIj8+PD9tc28tYXBwbGljYXRpb24gcHJvZ2lkPSJFeGNlbC5TaGVldCI/PjxXb3JrYm9vayB4bWxucz0idXJuOnNjaGVtYXMtbWljcm9zb2Z0LWNvbTpvZmZpY2U6c3ByZWFkc2hlZXQiIHhtbG5zOnhzaT0iaHR0cDovL3d3dy53My5vcmcvMjAwMS9YTUxTY2hlbWEtaW5zdGFuY2UiIHhtbG5zOng9InVybjpzY2hlbWFzLW1pY3Jvc29mdC1jb206b2ZmaWNlOmV4Y2VsIiB4bWxuczp4Mj0iaHR0cDovL3NjaGVtYXMubWljcm9zb2Z0LmNvbS9vZmZpY2UvZXhjZWwvMjAwMy94bWwiIHhtbG5zOnNzPSJ1cm46c2NoZW1hcy1taWNyb3NvZnQtY29tOm9mZmljZTpzcHJlYWRzaGVldCIgeG1sbnM6bz0idXJuOnNjaGVtYXMtbWljcm9zb2Z0LWNvbTpvZmZpY2U6b2ZmaWNlIiB4bWxuczpodG1sPSJodHRwOi8vd3d3LnczLm9yZy9UUi9SRUMtaHRtbDQwIiB4bWxuczpjPSJ1cm46c2NoZW1hcy1taWNyb3NvZnQtY29tOm9mZmljZTpjb21wb25lbnQ6c3ByZWFkc2hlZXQiPjxPZmZpY2VEb2N1bWVudFNldHRpbmdzIHhtbG5zPSJ1cm46c2NoZW1hcy1taWNyb3NvZnQtY29tOm9mZmljZTpvZmZpY2UiPjwvT2ZmaWNlRG9jdW1lbnRTZXR0aW5ncz48RXhjZWxXb3JrYm9vayB4bWxucz0idXJuOnNjaGVtYXMtbWljcm9zb2Z0LWNvbTpvZmZpY2U6ZXhjZWwiPjwvRXhjZWxXb3JrYm9vaz48V29ya3NoZWV0IHNzOk5hbWU9InN0b25lX3NoaXBtZW50X3N1bW1hcnkueG1sIj48VGFibGU+PFJvdz48Q2VsbD48RGF0YSBzczpUeXBlPSJTdHJpbmciPlN0b25lIFR5cGU8L0RhdGE+PC9DZWxsPjxDZWxsPjxEYXRhIHNzOlR5cGU9IlN0cmluZyI+VG90YWwgQ2FyYXQ8L0RhdGE+PC9DZWxsPjxDZWxsPjxEYXRhIHNzOlR5cGU9IlN0cmluZyI+VG90YWwgUVRZPC9EYXRhPjwvQ2VsbD48L1Jvdz48Um93PjxDZWxsPjxEYXRhIHNzOlR5cGU9IlN0cmluZyI+QW1ldGh5c3QgQ2Fib2Nob24gKDEpPC9EYXRhPjwvQ2VsbD48Q2VsbD48RGF0YSBzczpUeXBlPSJOdW1iZXIiPjkzLjY8L0RhdGE+PC9DZWxsPjxDZWxsPjxEYXRhIHNzOlR5cGU9Ik51bWJlciI+NjwvRGF0YT48L0NlbGw+PC9Sb3c+PC9UYWJsZT48L1dvcmtzaGVldD48L1dvcmtib29rPg==',
      file_name: 'stone_shipment_summary.xml',
      mime_type: 'application/xml',
    });
  }
}

export default USE_API_MAGENTO_MOCK_DATA ? new StoneServiceMock() : new StoneService();
